import { css, CSSProp } from 'styled-components';
import { ButtonProps } from 'antd';

type CSS = CSSProp;

interface FlexProps {
  align?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline' | string;
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly' | string;
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse' | 'initial' | string;
}

interface SidebarSizes {
  collapsedWidth: string;
  width: string;
}

interface Screens {
  xs?: boolean;
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
  xl?: boolean;
  xxl?: boolean;
}

interface TableWidthSettings {
  actionMinWidth: number;
  rowSelectionWidth: number;
}

interface ZIndexes {
  mobileTableActionsZIndex: number;
  attachmentDetailsZIndex: number;
  quickSearchZIndex: number;
  antMessageZIndex: number;
  toxEditorZIndex: number;
  fullscreenModeZIndex: number;
  headerZIndex: number;
  mobileFilterFooterZIndex: number;
  contentOverlayZIndex: number;
  siderMenuZIndex: number;
  floatingButtonZIndex: number;
}

type OpacityValue = string | number;

interface MediaQueries {
  [key: string]: (...args: CSSProp[]) => CSS;
}

/**
 * Creates text truncation CSS props.
 * @returns {CSS} If text is not fitting in block, three dots will be added in the end.
 */
export function truncate(): CSS {
  return css`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  `;
}

/**
 * Common use box shadow
 * @param {string} color - The color for the box shadow.
 * @returns {string} box shadow style
 */
export const getBoxShadow = (color: string): string => `0 3px 6px ${color}`;

/**
 * Creates media queries' numbers
 * @returns {Record<string, number>}
 */
export const sizes: Record<string, number> = {
  xs: 575,
  sm: 767,
  md: 991,
  lg: 1024,
  xl: 1200,
};

/**
 * Creates media queries from sizes object.
 * @returns {MediaQueries}
 */
export const media: MediaQueries = Object.keys(sizes).reduce(
  (acc, label) => ({
    ...acc,
    [label]: (...args: Parameters<typeof css>) => css`
      @media only screen and (max-width: ${sizes[label as keyof typeof sizes]}px) {
        ${css(...args)};
      }
    `,
  }),
  {},
);

/**
 * Creates sticky container
 * @returns {CSS}
 */
export function stickyContainer(): CSS {
  // Use generic for styled-components props
  return css`
    display: flex;
    align-items: center;
    background: ${(props) => props.theme.colors.secondaryBg};
    justify-content: space-between;
    position: sticky;
    top: 0;
    bottom: -1px;
    width: 100%;
    left: 0;
    height: 4.57rem;
    padding: 0 4rem;
    box-shadow: 0 -4px 6px 0px #00000010;
    z-index: 100;
    font-size: 1rem;
    &:only-child {
      justify-content: flex-end;
    }
    .ant-btn {
      margin-bottom: 0 !important;
    }

    ${media.sm`
      padding: 0 1.14rem;
      justify-content: flex-end;
    `}
  `;
}

/**
 * Creates flex container
 * @param {FlexProps} [flex] - Flex properties: align, justify, and direction.
 * @returns {CSS}
 */
export function flexContainer(
  flex: FlexProps = {
    align: 'center',
    justify: 'space-between',
    direction: 'initial',
  },
): CSS {
  const { align, justify, direction } = flex;
  return css`
    display: flex;
    align-items: ${align};
    justify-content: ${justify};
    flex-direction: ${direction};
  `;
}

/**
 * Center the text
 * @returns {CSS}
 */
export function textAlignCenter(): CSS {
  return css`
    text-align: center;
  `;
}

/**
 * Button heights
 * @param {ButtonSize} size - The size key ('large', 'medium', 'small').
 * @returns {string | undefined}
 */
export const getButtonHeight = (size: NonNullable<ButtonProps['size']>): string | undefined => {
  const details = {
    large: '1.28rem',
    middle: '1.14rem',
    small: '1rem',
  } as const;
  return details[size];
};

/**
 * Sidebar sizes
 * @returns {SidebarSizes}
 */
export const getSidebarSizes = (): SidebarSizes => ({
  collapsedWidth: '4.6rem',
  width: '20.7rem',
});

/**
 * Admin table actions icon's size
 * @param {Screens} screens - Object containing boolean flags for active breakpoints.
 * @returns {number}
 */
export const getResponsiveIconSize = (screens: Screens): number => {
  if (screens.xl) {
    return 43;
  }
  if (screens.md) {
    return 37;
  }
  return 38;
};

/**
 * Get table useful widths
 * @returns {TableWidthSettings}
 */
export const getTableWidthSettings = (): TableWidthSettings => ({
  actionMinWidth: 98,
  rowSelectionWidth: 40,
});

/**
 * Get z-indexes for components
 * @returns {ZIndexes}
 */
export const getZIndexes = (): ZIndexes => ({
  mobileTableActionsZIndex: 2099,
  attachmentDetailsZIndex: 2090,
  quickSearchZIndex: 2070,
  antMessageZIndex: 2008,
  toxEditorZIndex: 2006,
  fullscreenModeZIndex: 2005,
  headerZIndex: 2004,
  mobileFilterFooterZIndex: 1051,
  contentOverlayZIndex: 1000,
  siderMenuZIndex: 500,
  floatingButtonZIndex: 100,
});

/**
 * Get the fixed width for the full-screen report filter.
 * @returns {string}
 */
export const getFullScreenReportFilterWidth = (): string => '600px';

/**
 * Appends an opacity value to a hex color string.
 *
 * @param {string} hexColor - The base color in 6-digit hex format.
 * @param {OpacityValue} [opacity] - The opacity portion to append.
 * @returns {string} The resulting 8-digit hex color string.
 */
export const withOpacity = (hexColor?: string, opacity: OpacityValue = 85): string | undefined => (!hexColor ? undefined : `${hexColor}${opacity}`);
